import { useState, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const testimonials = [
  {
    quote: "Joe's briefing to our board was the single most effective catalyst for our AI strategy. He didn't sell us a thing—he gave us the clarity and confidence to move. That's the ultimate differentiator.",
    name: "Sarah J. Mitchell",
    title: "Chief Strategy Officer",
    company: "Global Insurance Firm",
  },
  {
    quote: "In a sea of AI hype, Joe is the calm, authoritative voice our partnership needed. His framework didn't just give us a strategy—it gave us a story we could tell our 12,000 employees.",
    name: "David Chen",
    title: "Chief Information Officer",
    company: "Fortune 100 Technology",
  },
  {
    quote: "We had 15 AI pilots and zero production. Six months after Joe's executive briefing, we had three in production and a workforce that was excited, not terrified. That's rare.",
    name: "Maria Rodriguez",
    title: "CEO",
    company: "Global Financial Services",
  },
  {
    quote: "Joe understands something most AI leaders don't: this isn't a technology problem, it's a trust problem. His Responsible AI framework was exactly what our board needed to greenlight our transformation.",
    name: "James Whitfield",
    title: "Board Director",
    company: "FTSE 100 Professional Services",
  },
];

export default function TestimonialSection() {
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const next = () => setIdx((i) => (i + 1) % testimonials.length);
  const prev = () => setIdx((i) => (i - 1 + testimonials.length) % testimonials.length);
  const t = testimonials[idx];

  return (
    <section data-testid="testimonial-section" ref={ref} className="py-24 md:py-36 bg-[#1E2229]/40">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <p className="text-xs uppercase tracking-[0.3em] text-[#C4963B] font-semibold mb-4">Proof in Action</p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-5xl leading-tight">
            What leaders are saying.
          </h2>
        </motion.div>

        <motion.div
          className="relative"
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          <div className="min-h-[260px] md:min-h-[240px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                className="max-w-3xl"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
              >
                <span className="text-[#C4963B]/30 text-5xl font-['Cormorant_Garamond'] leading-none select-none">
                  &ldquo;
                </span>
                <p className="font-['Cormorant_Garamond'] text-xl md:text-2xl lg:text-3xl italic leading-relaxed text-[#F9F6F0] -mt-4 mb-8">
                  {t.quote}
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-[#C4963B]/15 flex items-center justify-center text-[#C4963B] font-['Cormorant_Garamond'] text-lg font-semibold">
                    {t.name[0]}
                  </div>
                  <div>
                    <p className="text-[#F9F6F0] font-medium text-sm">{t.name}</p>
                    <p className="text-[#E8E6E1]/35 text-xs">
                      {t.title}, {t.company}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex items-center gap-3 mt-10">
            <button
              data-testid="testimonial-prev-btn"
              onClick={prev}
              className="w-10 h-10 border border-white/[0.08] flex items-center justify-center hover:border-[#C4963B] hover:text-[#C4963B] transition-colors duration-300 text-[#E8E6E1]/40"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              data-testid="testimonial-next-btn"
              onClick={next}
              className="w-10 h-10 border border-white/[0.08] flex items-center justify-center hover:border-[#C4963B] hover:text-[#C4963B] transition-colors duration-300 text-[#E8E6E1]/40"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <span className="text-[11px] text-[#E8E6E1]/25 ml-3 tabular-nums">
              {String(idx + 1).padStart(2, "0")} / {String(testimonials.length).padStart(2, "0")}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
